import { type CaptureFileJSONObject, IkeCaptureType } from '@ikegps/core-api';
import { Log } from 'bblog';
import { action, computed, makeObservable, observable } from 'mobx';
import { IkeId } from '../id';
import { Geometry3D } from '../math';
import {
	CollectionModel,
	CollectionModelBrightnessCorrectionState,
	CollectionModelCropParameterState,
	CollectionModelDrawableLine,
	CollectionModelDrawableMarker,
	CollectionModelDrawableMarkerOffset,
	CollectionModelDrawableState,
	CollectionModelDrawableVerticalSeparation,
	CollectionModelFreeCropParameter,
	CollectionModelGammaBrightnessCorrection,
	CollectionModelImageVisibilityState,
} from '../model';
import { CollectionModelDrawable } from '../model/collection/drawable/drawable';
import { IkeDrawState } from './draw.state';
import { IkeDrawUtil } from './draw.util';
import { SVGEventHandler } from './draw/svg/svg.event';
import type { SVGRenderOptions } from './draw/svg/svg.render';
import { SVGWheel } from './draw/svg/svg.wheel';
import { FormModelFieldSubForm } from '../model/form/subform/form.subform';

export interface DrawContextConfigOptions {
	units: string;
	interactive: boolean;
}

export interface DrawContextConfig {
	log: Log;
	collection: CollectionModel;
	capture: CaptureFileJSONObject;
	window?: Window;
	/**
	 * Async function to fetch the mask for a capture
	 */
	captureMaskFetcher?: (collectionId: string, captureId: string) => Promise<string>;

	onStartHOASession?: () => void;
}

export class IkeDraw {
	id = 'ds_' + IkeId.generate();

	window: Window | null;

	state: IkeDrawState;

	collection: CollectionModel;
	capture: CaptureFileJSONObject;
	@observable isMarkable: boolean = false;

	get drawState(): CollectionModelDrawableState {
		// DrawableStates are frequently discarded (such as when discarding edits),
		// so fetch them on demand to avoid leaks.
		return this.collection.getDrawableState(this.capture.objectId);
	}

	brightnessCorrectionState: CollectionModelBrightnessCorrectionState;
	imageVisibilityState: CollectionModelImageVisibilityState;
	cropParameterState: CollectionModelCropParameterState;
	orientation: 'landscape' | 'portrait';
	/** List of images already loaded by the browser */
	imageMap = observable.map<string, boolean>(null, { deep: false, name: `Draw-ImageLoaded-${this.id}` });

	boundEvents: SVGEventHandler;
	boundWheelEvent: (el: any) => any;

	captureMaskFetcher?: (collectionId: string, captureId: string) => Promise<string>;

	log: Log;

	constructor(ctx: DrawContextConfig) {
		makeObservable(this);

		this.log = ctx.log;
		this.collection = ctx.collection;
		this.capture = ctx.capture;
		this.brightnessCorrectionState = this.collection.getBrightnessCorrectionState(ctx.capture.objectId);
		this.imageVisibilityState = this.collection.getImageVisibilityState(ctx.capture.objectId);
		this.cropParameterState = this.collection.getCropParameterState(ctx.capture.objectId);
		this.state = new IkeDrawState(this);
		this.window = ctx.window ?? null;
		if (ctx.captureMaskFetcher) {
			this.captureMaskFetcher = ctx.captureMaskFetcher;
		}
		this.orientation =
			this.capture.resolution.width > this.capture.resolution.height ? 'landscape' : ('portrait' as const);
		this.init();
		this.boundWheelEvent = (el) => SVGWheel.onWheel(this, el);
		this.boundEvents = new SVGEventHandler(this, ctx.onStartHOASession);
	}

	_lastUnits: string | null = null;
	_getLabelText: ((drawId: string, ctx: SVGRenderOptions) => string[]) | null = null;
	/** Use a transformer to cache the drawableId to text conversions they dont change very often. */

	getLabelText(drawableId: string, options: SVGRenderOptions) {
		if (options == null || options.units == null) {
			return [];
		}
		const units = options.units;
		if (this._lastUnits !== units) {
			this._lastUnits = units;
			this._getLabelText = (drawId: string, ctx: SVGRenderOptions) => {
				const drawable = this.drawState.getDrawable(drawId);

				if (CollectionModelDrawableMarker.isMarker(drawable)) {
					return IkeDrawUtil.getMarkerLabelText(this.capture, drawable, ctx);
				}

				if (CollectionModelDrawableMarkerOffset.isBaseOffset(drawable)) {
					return IkeDrawUtil.getBaseOffsetLabelText(this.capture, drawable);
				}

				if (CollectionModelDrawableLine.isLine(drawable)) {
					return IkeDrawUtil.getLineLabelText(drawable, ctx);
				}

				if (drawable instanceof CollectionModelDrawableVerticalSeparation) {
					return drawable.label.text;
				}

				return [];
			};
		}

		return this._getLabelText?.(drawableId, options) ?? [];
	}

	/**
	 * Returns an array of the first 4 colors of spans if the Marker belongs to Span
	 * @param drawableId formID from marker
	 */
	getSpanColors(drawableId: string): string[] | null {
		const drawable = this.drawState.getDrawable(drawableId) as CollectionModelDrawableMarker;
		const label = drawable.label;
		const labelLinks = label.links;

		let colors: string[] = [];

		for (const link of labelLinks) {
			const color = FormModelFieldSubForm.getSpanColor(link.parent);
			if (color && colors.indexOf(color) === -1) {
				colors.push(color);
			}
		}
		return colors ? colors.slice(0, 4) : null;
	}

	getBrightness(brightnessCorrectionId: string) {
		const brightnessCorrection = this.brightnessCorrectionState.getBrightnessCorrection(brightnessCorrectionId);
		if (CollectionModelGammaBrightnessCorrection.isGamma(brightnessCorrection)) {
			return brightnessCorrection.exponent;
		}
		return 1;
	}

	getCrop(cropParameterId: string) {
		const cropParameter = this.cropParameterState.getCropParameter(cropParameterId);
		if (CollectionModelFreeCropParameter.isFree(cropParameter)) {
			return cropParameter;
		}
		return 1;
	}

	@action setImageLoaded(imageId: string, loaded = true) {
		if (this.imageMap.has(imageId)) {
			return;
		}

		this.imageMap.set(imageId, loaded);
	}

	/** Pre load the image into the browser cache */
	loadImage(imageId: string, url: string) {
		if (this.imageMap.has(imageId)) {
			return;
		}
		const image = new Image();
		image.onload = () => this.setImageLoaded(imageId);
		image.onerror = () => this.setImageLoaded(imageId, false);
		image.src = url;
	}

	isImageReady(imageId: string): boolean {
		return this.imageMap.get(imageId) === true;
	}

	@action init() {
		if (this.capture != null) {
			if (this.state.cropHeight > 0 && this.state.cropWidth > 0 && !this.isMarkable) {
				this.state.setCenter(
					this.state.cropX + this.state.cropWidth * 0.5,
					this.state.cropY + this.state.cropHeight * 0.5
				);
			} else {
				this.state.setCenter(this.capture.resolution.width / 2, this.capture.resolution.height / 2);
			}
		}

		this.state.selectedId = null;
		this.state.hoverId = null;
		this.state.setTool(null);
		this.state.setCrosshair(false);

		if (this.window) {
			this.state.isDateLabelVisible = false;
			this.state.isBearingLabelVisible = false;
		} else {
			this.state.isDateLabelVisible = true;
			this.state.isBearingLabelVisible = true;
		}

		if (!this.isIkePhoto) {
			this.state.setCrosshair(true);
		} else if (!this.drawState.hasUserMeasurements()) {
			this.state.setCrosshair(true);
		}
	}

	get geometry3d(): Geometry3D {
		return this.collection.state.getGeometry(this.capture.objectId);
	}

	/** Does this draw state have enough information for photo measure */
	@computed get isIkePhoto(): boolean {
		return this.capture.type === IkeCaptureType.TRUE_SIZE;
	}

	/** are any elements on the page currently moving */
	@computed get isMoving(): boolean {
		return this.state.moveId != null;
	}

	@computed get isLinking(): boolean {
		return this.state.linkFieldIds.length > 0;
	}

	@computed get selected(): CollectionModelDrawable {
		if (this.state.selectedId == null) {
			return null;
		}

		if (this.state.selectedId === 'image') {
			return null;
		}

		if (!this.drawState.hasDrawable(this.state.selectedId)) {
			return null;
		}
		return this.drawState.getDrawable(this.state.selectedId);
	}

	@computed get highlighted(): CollectionModelDrawable {
		if (this.state.hoverId == null) {
			return null;
		}
		if (this.state.hoverId === 'image') {
			return null;
		}

		if (!this.drawState.hasDrawable(this.state.hoverId)) {
			return null;
		}

		return this.drawState.getDrawable(this.state.hoverId);
	}
}
